import { useState } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ToolCard } from "./ToolCard";

interface PipelineSubmissionArgs {
  run_name: string;
  workflow_type: string;
  inputs: Record<string, unknown>;
  output_location?: string;
  workbench_id?: number;
  workflow_source_uri?: string;
  engine_specific_parameters?: Record<string, unknown>;
  error?: string;
}

const SUBMISSION_CONFIRMED = JSON.stringify({ kind: "approved" });
const SUBMISSION_CANCELLED = JSON.stringify({ kind: "denied" });

const PipelineConfirmationRender: ToolCallMessagePartComponent<PipelineSubmissionArgs, string> = ({
  args,
  result,
  addResult,
}) => {
  const [decision, setDecision] = useState<"confirmed" | "cancelled" | null>(null);

  const isLoading = !args || typeof args.run_name !== "string";
  const isSubmitted = decision !== null || result != null;
  const effectiveDecision = isSubmitted
    ? (decision ?? (result === SUBMISSION_CONFIRMED ? "confirmed" : "cancelled"))
    : null;

  const handleConfirm = () => {
    if (decision) return;
    setDecision("confirmed");
    addResult(SUBMISSION_CONFIRMED);
  };

  const handleCancel = () => {
    if (decision) return;
    setDecision("cancelled");
    addResult(SUBMISSION_CANCELLED);
  };

  if (!isLoading && args.error) {
    return (
      <div className="border border-red-200 rounded-lg p-4 mb-2 bg-red-50/50">
        <div className="flex items-center gap-2 mb-3">
          <XCircle className="size-4 text-red-600" />
          <span className="text-sm font-medium text-red-600">Pipeline Validation Error</span>
        </div>
        <p className="text-sm text-red-700">{args.error}</p>
      </div>
    );
  }

  return (
    <ToolCard
      label="Pipeline Submission"
      color="indigo"
      isLoading={isLoading}
      loadingText="Preparing pipeline submission..."
      isSubmitted={isSubmitted}
    >
      {args && <SubmissionDetails args={args} />}

      {isSubmitted ? (
        <div className="flex items-center gap-2 text-sm mt-3">
          {effectiveDecision === "confirmed" ? (
            <>
              <CheckCircle2 className="size-4 text-green-600" />
              <span className="text-green-600">Submission confirmed</span>
              {result == null && <Loader2 className="size-3.5 animate-spin ml-1" />}
            </>
          ) : (
            <>
              <XCircle className="size-4 text-gray-500" />
              <span className="text-gray-500">Submission cancelled</span>
            </>
          )}
        </div>
      ) : (
        <div className="flex gap-2 mt-4">
          <Button size="sm" onClick={handleConfirm} className="gap-1.5">
            <CheckCircle2 className="size-3.5" />
            Confirm & Submit
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      )}
    </ToolCard>
  );
};

function SubmissionDetails({ args }: { args: PipelineSubmissionArgs }) {
  const inputCount = args.inputs ? Object.keys(args.inputs).length : 0;

  return (
    <div className="space-y-2">
      <DetailRow label="Run Name" value={args.run_name} />
      <DetailRow label="Workflow Type" value={args.workflow_type} />
      {args.output_location && <DetailRow label="Output Location" value={args.output_location} />}
      {args.workbench_id != null && (
        <DetailRow label="Workbench" value={String(args.workbench_id)} />
      )}
      {args.workflow_source_uri && (
        <DetailRow label="Workflow Source" value={args.workflow_source_uri} />
      )}

      {inputCount > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Inputs ({inputCount})
          </span>
          <div className="mt-1 rounded bg-gray-50 p-2 text-xs font-mono overflow-x-auto max-h-40 overflow-y-auto">
            {Object.entries(args.inputs).map(([key, value]) => (
              <div key={key} className="flex gap-2 py-0.5">
                <span className="text-gray-500 shrink-0">{key}:</span>
                <span className="text-gray-900 break-all">
                  {typeof value === "string" ? value : JSON.stringify(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {args.engine_specific_parameters &&
        Object.keys(args.engine_specific_parameters).length > 0 && (
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Engine Parameters
            </span>
            <div className="mt-1 rounded bg-gray-50 p-2 text-xs font-mono overflow-x-auto">
              {Object.entries(args.engine_specific_parameters).map(([key, value]) => (
                <div key={key} className="flex gap-2 py-0.5">
                  <span className="text-gray-500 shrink-0">{key}:</span>
                  <span className="text-gray-900 break-all">
                    {typeof value === "string" ? value : JSON.stringify(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 shrink-0 min-w-[120px]">{label}:</span>
      <span className="text-gray-900 break-all">{value}</span>
    </div>
  );
}

export const PipelineConfirmationTool = makeAssistantToolUI<PipelineSubmissionArgs, string>({
  toolName: "pipeline_submit_run",
  render: PipelineConfirmationRender,
});
